import React, { useState } from 'react';
import type {
  AchadoClinicoAnatomicoContract,
  SistemaAnatomico,
  RegiaoCorpoAnatomica,
  AnaliseFisionomicaFotoContract,
} from '@sstexames/shared-types';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CameraIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

interface Props {
  achados: AchadoClinicoAnatomicoContract[];
  analiseFisionomica?: AnaliseFisionomicaFotoContract;
  scoreQualidade?: number;
  scoreAcidente?: number;
}

interface RegiaoConfig {
  key: RegiaoCorpoAnatomica;
  label: string;
  cx: number;
  cy: number;
  r: number;
  sistemaPadrao: SistemaAnatomico;
}

const REGIOES_ANATOMICAS: RegiaoConfig[] = [
  { key: 'CABECA_CRANIO', label: 'Cabeça & Sistema Nervoso', cx: 100, cy: 38, r: 12, sistemaPadrao: 'NERVOSO' },
  { key: 'OLHOS', label: 'Olhos & Acuidade Visual', cx: 94, cy: 35, r: 6, sistemaPadrao: 'VISUAL' },
  { key: 'OUVIDOS', label: 'Ouvidos & Limiar Auditivo', cx: 114, cy: 37, r: 6, sistemaPadrao: 'AUDITIVO' },
  { key: 'PESCOCO', label: 'Pescoço & Região Cervical', cx: 100, cy: 68, r: 7, sistemaPadrao: 'MUSCULOESQUELETICO' },
  { key: 'TORAX_CORACAO', label: 'Tórax & Sistema Cardiovascular', cx: 92, cy: 112, r: 10, sistemaPadrao: 'CARDIOVASCULAR' },
  { key: 'PULMOES', label: 'Pulmões & Sistema Respiratório', cx: 112, cy: 110, r: 11, sistemaPadrao: 'RESPIRATORIO' },
  { key: 'COLUNA_LOMBAR', label: 'Coluna Lombar & Postura', cx: 100, cy: 172, r: 10, sistemaPadrao: 'MUSCULOESQUELETICO' },
  { key: 'BRACOS_MAOS', label: 'Membros Superiores & Mãos', cx: 48, cy: 148, r: 11, sistemaPadrao: 'MUSCULOESQUELETICO' },
  { key: 'ABDOMEN', label: 'Abdômen & Metabolismo', cx: 100, cy: 145, r: 13, sistemaPadrao: 'METABOLICO' },
  { key: 'PERNAS_PES', label: 'Membros Inferiores & Joelhos', cx: 118, cy: 285, r: 12, sistemaPadrao: 'MUSCULOESQUELETICO' },
];

export default function MapaAnatomicoCorporal({
  achados,
  analiseFisionomica,
}: Props) {
  const [selectedSistema, setSelectedSistema] = useState<SistemaAnatomico | null>(null);
  const [selectedRegiao, setSelectedRegiao] = useState<RegiaoCorpoAnatomica | null>(null);

  const sistemasComAchados = new Set(achados.map((a) => a.sistema));
  const regioesComAchados = new Set(
    achados.map((a) => a.regiao_corpo).filter((r): r is RegiaoCorpoAnatomica => Boolean(r)),
  );

  const getAchadosRegiao = (regiao: RegiaoCorpoAnatomica) => {
    return achados.filter((a) => a.regiao_corpo === regiao);
  };

  const getCorRegiao = (regiao: RegiaoCorpoAnatomica) => {
    const achadosDaRegiao = getAchadosRegiao(regiao);
    if (achadosDaRegiao.length === 0) return '#64748B'; // Slate neutro
    if (achadosDaRegiao.some((a) => a.gravidade === 'SEVERA')) return '#EF4444'; // Red 500
    if (achadosDaRegiao.some((a) => a.gravidade === 'MODERADA')) return '#F59E0B'; // Amber 500
    return '#38BDF8'; // Sky 400 (Leve)
  };

  const getCorSistema = (sistema: SistemaAnatomico) => {
    const achadosSis = achados.filter((a) => a.sistema === sistema);
    if (achadosSis.length === 0) return '#94A3B8';
    if (achadosSis.some((a) => a.gravidade === 'SEVERA')) return '#EF4444';
    if (achadosSis.some((a) => a.gravidade === 'MODERADA')) return '#F59E0B';
    return '#3B82F6';
  };

  const getStatusBadge = (gravidade: string) => {
    if (gravidade === 'SEVERA') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800">
          <ExclamationTriangleIcon className="w-3.5 h-3.5 mr-1" />
          Atenção Pericial (Severa)
        </span>
      );
    }
    if (gravidade === 'MODERADA') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">
          <ExclamationTriangleIcon className="w-3.5 h-3.5 mr-1" />
          Acompanhamento Clínico (Moderada)
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800">
        <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
        Vigilância Preventiva (Leve)
      </span>
    );
  };

  const achadosFiltrados = achados.filter((a) => {
    if (selectedRegiao && a.regiao_corpo !== selectedRegiao) return false;
    if (selectedSistema && a.sistema !== selectedSistema) return false;
    return true;
  });

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-6">
      {/* Header com badges de conformidade e legenda */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-gray-100 gap-3">
        <div>
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheckIcon className="w-5 h-5 text-indigo-600" />
            Mapa Anatômico Corporal 360° & Vigilância Biológica
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Mapeamento dinâmico de órgãos e aparelhos correlacionando exames clínicos, complementares e atestados.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5 text-gray-600">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> Severa
          </span>
          <span className="inline-flex items-center gap-1.5 text-gray-600">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Moderada
          </span>
          <span className="inline-flex items-center gap-1.5 text-gray-600">
            <span className="w-2.5 h-2.5 rounded-full bg-sky-400" /> Leve
          </span>
          <span className="inline-flex items-center gap-1.5 text-gray-400">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-300" /> Sem achados
          </span>
        </div>
      </div>

      {/* Grid Principal: Avatar Anatômico + Detalhamento de Sistemas */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Painel Esquerdo: Silhueta Anatômica Humanizada */}
        <div className="lg:col-span-5 flex flex-col items-center justify-center p-5 bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 rounded-2xl border border-indigo-900 shadow-inner relative overflow-hidden">
          {/* Efeito Glow de Fundo */}
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

          {/* SVG Anatômico com Traçado Humano Proporcional */}
          <svg
            viewBox="0 0 200 400"
            className="w-52 h-96 select-none relative z-10"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="bodyGradientCustomer" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1E293B" />
                <stop offset="50%" stopColor="#334155" />
                <stop offset="100%" stopColor="#1E293B" />
              </linearGradient>
              <filter id="glowCustomer" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

            {/* Silhueta Corporal com Curvatura Muscular e Proporções Realistas */}
            <path
              d="
                M 100 15
                C 114 15, 122 24, 122 40
                C 122 52, 116 62, 108 66
                C 112 68, 124 74, 138 82
                C 148 88, 158 98, 164 122
                C 168 138, 160 178, 154 196
                C 152 202, 146 202, 144 196
                C 142 182, 142 152, 138 136
                C 134 146, 132 165, 130 190
                C 128 212, 126 230, 132 258
                C 136 278, 138 316, 134 358
                C 132 378, 134 388, 120 388
                C 112 388, 110 376, 110 354
                C 110 326, 106 288, 104 252
                C 102 240, 101 228, 100 228
                C 99 228, 98 240, 96 252
                C 94 288, 90 326, 90 354
                C 90 376, 88 388, 80 388
                C 66 388, 68 378, 66 358
                C 62 316, 64 278, 68 258
                C 74 230, 72 212, 70 190
                C 68 165, 66 146, 62 136
                C 58 152, 58 182, 56 196
                C 54 202, 48 202, 46 196
                C 40 178, 32 138, 36 122
                C 42 98, 52 88, 62 82
                C 76 74, 88 68, 92 66
                C 84 62, 78 52, 78 40
                C 78 24, 86 15, 100 15
                Z
              "
              fill="url(#bodyGradientCustomer)"
              stroke="#475569"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />

            {/* Linhas de Tensão Anatômica */}
            <path
              d="M 100 68 L 100 220"
              stroke="#64748B"
              strokeWidth="1"
              strokeDasharray="2 3"
              opacity="0.6"
            />
            <path
              d="M 70 88 C 100 96, 100 96, 130 88"
              stroke="#64748B"
              strokeWidth="1"
              strokeDasharray="2 2"
              fill="none"
              opacity="0.5"
            />
            <ellipse
              cx="100"
              cy="110"
              rx="22"
              ry="18"
              fill="none"
              stroke="#64748B"
              strokeWidth="0.8"
              strokeDasharray="3 3"
              opacity="0.4"
            />

            {/* Pontos de Vigilância Anatômica com Indicadores e Animação */}
            {REGIOES_ANATOMICAS.map((reg) => {
              const temAchado = regioesComAchados.has(reg.key);
              const cor = getCorRegiao(reg.key);
              const isSelected = selectedRegiao === reg.key;

              return (
                <g
                  key={reg.key}
                  className="cursor-pointer transition-all duration-300 group"
                  onClick={() => {
                    setSelectedRegiao(isSelected ? null : reg.key);
                    setSelectedSistema(null);
                  }}
                >
                  {/* Halo Pulsante para Regiões com Achados */}
                  {temAchado && (
                    <circle
                      cx={reg.cx}
                      cy={reg.cy}
                      r={reg.r + 5}
                      fill={cor}
                      opacity={isSelected ? '0.45' : '0.2'}
                      className="animate-ping"
                      style={{ animationDuration: '3s' }}
                    />
                  )}

                  {/* Círculo de Seleção Ativa */}
                  {isSelected && (
                    <circle
                      cx={reg.cx}
                      cy={reg.cy}
                      r={reg.r + 4}
                      fill="none"
                      stroke="#FFFFFF"
                      strokeWidth="2"
                      strokeDasharray="3 2"
                    />
                  )}

                  {/* Hotspot Central */}
                  <circle
                    cx={reg.cx}
                    cy={reg.cy}
                    r={reg.r}
                    fill={cor}
                    filter="url(#glowCustomer)"
                    className="transition-transform group-hover:scale-125"
                    stroke="#FFFFFF"
                    strokeWidth={isSelected ? '2' : '1'}
                    opacity={isSelected ? '1' : temAchado ? '0.95' : '0.7'}
                  />

                  {/* Ponto Central de Foco */}
                  <circle cx={reg.cx} cy={reg.cy} r="2" fill="#FFFFFF" />
                </g>
              );
            })}
          </svg>

          {/* Feedback e Botão de Limpeza do Filtro */}
          <div className="w-full text-center mt-3 z-10">
            {selectedRegiao ? (
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/20 rounded-full border border-indigo-400/30 text-indigo-200 text-xs">
                <span>Foco: <strong>{REGIOES_ANATOMICAS.find((r) => r.key === selectedRegiao)?.label}</strong></span>
                <button
                  onClick={() => setSelectedRegiao(null)}
                  className="text-indigo-300 hover:text-white underline ml-1 text-[11px]"
                >
                  Limpar
                </button>
              </div>
            ) : (
              <p className="text-[11px] text-slate-400">
                Toque nos pontos brilhantes do avatar para inspecionar cada região corporal.
              </p>
            )}
          </div>
        </div>

        {/* Painel Direito: Filtro por Sistema Biológico & Cards dos Achados */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          {/* Barra de Filtro Rápido por Aparelho/Sistema */}
          <div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
              Filtrar por Sistema Biológico
            </span>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => {
                  setSelectedSistema(null);
                  setSelectedRegiao(null);
                }}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                  selectedSistema === null && selectedRegiao === null
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todos ({achados.length})
              </button>
              {(
                [
                  'CARDIOVASCULAR',
                  'RESPIRATORIO',
                  'MUSCULOESQUELETICO',
                  'AUDITIVO',
                  'VISUAL',
                  'METABOLICO',
                  'NERVOSO',
                  'DIGESTIVO',
                  'DERMATOLOGICO',
                  'GERAL',
                ] as SistemaAnatomico[]
              ).map((sis) => {
                const temAchado = sistemasComAchados.has(sis);
                const isSelected = selectedSistema === sis;
                return (
                  <button
                    key={sis}
                    onClick={() => {
                      setSelectedSistema(isSelected ? null : sis);
                      setSelectedRegiao(null);
                    }}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                      isSelected
                        ? 'bg-indigo-600 text-white shadow-sm ring-2 ring-indigo-300'
                        : temAchado
                        ? 'bg-indigo-50 text-indigo-800 hover:bg-indigo-100 border border-indigo-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: getCorSistema(sis) }}
                    />
                    {sis.charAt(0) + sis.slice(1).toLowerCase()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Lista de Achados Clínicos Filtrados */}
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {achadosFiltrados.length === 0 ? (
              <div className="p-6 bg-slate-50 rounded-xl border border-dashed border-slate-300 text-center text-xs text-gray-500">
                <CheckCircleIcon className="w-8 h-8 text-emerald-500 mx-auto mb-1.5 opacity-80" />
                Nenhum achado adverso ou restrição registrada para o filtro selecionado. Todos os parâmetros em conformidade com o PCMSO.
              </div>
            ) : (
              achadosFiltrados.map((item) => (
                <div
                  key={item.id}
                  className="p-3.5 bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-200 transition-colors shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-gray-900">
                          {item.titulo}
                        </span>
                        {getStatusBadge(item.gravidade)}
                        {item.regiao_corpo && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 text-slate-700">
                            Região: {item.regiao_corpo}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                        {item.descricao}
                      </p>
                      {item.detalhe_clinico && item.detalhe_clinico !== item.descricao && (
                        <p className="text-[11px] text-indigo-700 bg-indigo-50/80 rounded px-2 py-1 mt-1.5 border border-indigo-100">
                          <strong>Parecer Clínico:</strong> {item.detalhe_clinico}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500">
                        <span>
                          <strong>Data:</strong> {new Date(item.data_identificacao).toLocaleDateString('pt-BR')}
                        </span>
                        <span>•</span>
                        <span>
                          <strong>Fonte Pericial:</strong> {item.fonte_documento}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Análise Fisionômica Facial (Foto de Check-in na Clínica) */}
          {analiseFisionomica && (
            <div className="p-3.5 bg-gradient-to-r from-slate-50 to-indigo-50/50 rounded-xl border border-indigo-100 flex items-center gap-4">
              <div className="relative shrink-0">
                {analiseFisionomica.selfie_url ? (
                  <img
                    src={analiseFisionomica.selfie_url}
                    alt="Comprovante de Presença"
                    className="w-14 h-14 rounded-xl object-cover border-2 border-indigo-400 shadow-sm"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center border border-indigo-200 text-indigo-600">
                    <CameraIcon className="w-6 h-6" />
                  </div>
                )}
                <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" title="Presença Confirmada" />
              </div>
              <div className="flex-1 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-900 flex items-center gap-1">
                    <SparklesIcon className="w-4 h-4 text-indigo-600" />
                    Biometria & Análise Fisionômica Facial
                  </span>
                  <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
                    Vitalidade: {analiseFisionomica.indicadores_faciais.estimativa_vitalidade_score}%
                  </span>
                </div>
                <p className="text-gray-600 mt-0.5 leading-snug">
                  {analiseFisionomica.correlacao_clinica}
                </p>
                <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500">
                  <span>Fadiga Facial: <strong>{analiseFisionomica.indicadores_faciais.aparencia_fadiga}</strong></span>
                  <span>•</span>
                  <span>Tensão Muscular: <strong>{analiseFisionomica.indicadores_faciais.tensao_muscular_facial}</strong></span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
